import {
  CHART_LIMITS,
  HEX_COLOUR_RE,
  REQUIRED_PALETTE_KEYS,
  type BaseChart,
  type ChartValidationIssue,
  type ChartValidationResult,
} from './chart.js';
import type { ManiaEngineTuning } from './engine/mania/core.js';
import { VARIANTS } from './gameTypes.js';
import {
  MANIA_LANE_COUNT,
  SAME_BEAT_EPSILON,
  isManiaLane,
  validateManiaHoldOverlaps,
  type ManiaLane,
  type ManiaNote,
} from './maniaNote.js';

/**
 * Y-position of the hit line as a fraction of canvas height. The hit
 * line is where falling tiles cross the input zone in Piano's
 * renderer. Shared with the engine via
 * {@link PIANO_PERFECT_OVERLAP_FRAC} so visual and timing layers
 * stay coupled.
 */
export const PIANO_HIT_LINE_Y_FRAC = 0.85;

/**
 * Tile height as a fraction of canvas height in Piano's renderer.
 * The renderer clamps to a min/max pixel range for extreme canvas
 * sizes, but for the typical playable range this fraction is
 * authoritative. Shared with the engine via
 * {@link PIANO_PERFECT_OVERLAP_FRAC} so the geometric perfect window
 * matches the visual.
 */
export const PIANO_TILE_HEIGHT_FRAC = 0.28;

/**
 * Geometric perfect-zone span as a fraction of the chart's
 * `approachMs` for Piano mode. The hit detector is visual rather
 * than a tight ms window: a tap counts as PERFECT for the full time
 * the tile's body overlaps the hit bar, from the leading edge first
 * touching the line to the trailing edge crossing it.
 *
 * Derived directly from the renderer's tile-height-to-hit-line ratio
 * so the engine and the player's eye agree on "the tile is on the
 * bar." The renderer's px clamps
 * (TILE_HEIGHT_MIN_PX/TILE_HEIGHT_MAX_PX in `pianoRenderer.ts`) can
 * cause a small drift on extreme canvas heights (<500px or
 * >~860px), but the typical playable range is exact.
 */
export const PIANO_PERFECT_OVERLAP_FRAC =
  PIANO_TILE_HEIGHT_FRAC / PIANO_HIT_LINE_Y_FRAC;

/**
 * Early-tap "good" window for keyboard input in Piano, as a fraction
 * of `approachMs`. Keyboard players have only timing to worry about —
 * the right key is trivial — so this window is intentionally tight
 * to keep some chance of a true miss when the tile is still high up
 * the lane. Above this threshold an early keyboard tap is an empty
 * tap (no resolution, eventual auto-miss).
 */
export const PIANO_KEYBOARD_GOOD_EARLY_FRAC = 0.15;

/**
 * Early-tap "good" window for pointer (touch / mouse) input in Piano,
 * as a fraction of `approachMs`. Pointer input couples lane choice
 * and vertical tile-targeting in one gesture (the player physically
 * has to touch the tile), so the timing tier is forgiving — any tap
 * on a visible tile in the lane resolves at least as a `good`. Set
 * to 1.0 so the entire visible approach counts: if you can see the
 * tile, you can tap it.
 */
export const PIANO_POINTER_GOOD_EARLY_FRAC = 1.0;

/**
 * Tolerance applied when validating that a tile's beat lines up with
 * an audio onset. Mirrors the Circles tolerance (60 ms) — piano is
 * melody-tracking so MIDI-imported notes already sit precisely on the
 * source's audible events, but synthesised charts pulled from onset
 * lists need a small buffer for rounding.
 */
export const PIANO_ONSET_TOLERANCE_MS = 60;

/**
 * Bundle of Piano-specific fractions the shared mania engine needs at
 * config-build time. Lives here (next to the fractions themselves)
 * rather than in a `pianoTuning.ts` so there's no extra hop when
 * tracing where Piano's geometric perfect zone comes from.
 */
export const PIANO_ENGINE_TUNING: ManiaEngineTuning = {
  perfectOverlapFrac: PIANO_PERFECT_OVERLAP_FRAC,
  keyboardGoodEarlyFrac: PIANO_KEYBOARD_GOOD_EARLY_FRAC,
  pointerGoodEarlyFrac: PIANO_POINTER_GOOD_EARLY_FRAC,
  // Piano is instadeath: a single missed tile ends the run, so a
  // flawless clear is the only win. Keeps runs short and the stakes
  // high instead of letting an HP buffer drag a sloppy attempt on.
  // The tutorial opts out (see PianoTutorialPage) so learners can
  // practise every tile type without a slip booting them.
  instadeathOnMiss: true,
};

/**
 * Hard cap on note count per chart. Generous: a 90-second extreme-variant
 * chart at 4 notes/sec averages ~360 notes, full-arrangement extreme
 * with chord layering and roll-subdivided holds tops out near ~1500.
 * 3000 leaves room for outlier songs without letting a malformed chart
 * wedge the engine.
 */
export const MAX_PIANO_NOTES = 3000;

/**
 * 4-lane falling-tile chart payload. Tiles flow down four columns
 * toward a hit bar; the difficulty ramp comes from chart density
 * (track depth + subdivision) rather than raw audio speed-up at
 * chart start. Loops 1–3 award cups via the shared
 * {@link import('./progression.js').cupsForGlobalProgress} model;
 * past loop 3 the {@link import('./progression.js').loopSpeedRamp}
 * keeps stepping the playback rate up until HP drains.
 */
export interface PianoChart extends BaseChart {
  mode: 'piano';
  notes: ManiaNote[];
  /**
   * Per-chart timing windows. Hard / Extreme variants tighten these
   * so harder maps demand more precise timing in addition to denser
   * patterns. When absent the engine falls back to the global
   * {@link import('./gameTypes.js').PERFECT_WINDOW_MS} /
   * {@link import('./gameTypes.js').GOOD_WINDOW_MS} so old fixtures
   * keep working.
   */
  perfectWindowMs?: number;
  goodWindowMs?: number;
  /**
   * How long a tile is visible before its hit beat. Renderer reads
   * this to size the spawn-to-hit-line travel; engine uses it to gate
   * "early click" handling. Wall-clock so muscle memory transfers
   * between songs at different BPMs.
   */
  approachMs: number;
}

/**
 * Per-chart authoring rule: every required (non-hold) tile must sit
 * within this many milliseconds of an actual audio onset. Mirrors
 * Pulse's PAD_ONSET_TOLERANCE_MS. Hold tails do not need to align with
 * an onset — they're sustained gameplay between two onsets.
 */
export function validatePianoChart(chart: PianoChart): ChartValidationResult {
  const issues: ChartValidationIssue[] = [];

  validateBaseFields(chart, issues);

  if (!Array.isArray(chart.notes) || chart.notes.length === 0) {
    issues.push({ level: 'error', message: 'Piano chart needs at least one note' });
  } else if (chart.notes.length > MAX_PIANO_NOTES) {
    issues.push({
      level: 'error',
      message: `notes ${chart.notes.length} exceeds limit ${MAX_PIANO_NOTES}`,
    });
  }

  if (
    !Number.isFinite(chart.approachMs) ||
    chart.approachMs < 200 ||
    chart.approachMs > 2500
  ) {
    issues.push({
      level: 'error',
      message: `approachMs ${chart.approachMs} must be in [200, 2500] ms`,
    });
  }

  const TIMING_WINDOW_MIN_MS = 5;
  const TIMING_WINDOW_MAX_MS = 500;
  for (const field of ['perfectWindowMs', 'goodWindowMs'] as const) {
    const value = chart[field];
    if (value === undefined) {
      continue;
    }
    if (
      !Number.isFinite(value) ||
      value < TIMING_WINDOW_MIN_MS ||
      value > TIMING_WINDOW_MAX_MS
    ) {
      issues.push({
        level: 'error',
        message: `${field} ${value} must be in [${TIMING_WINDOW_MIN_MS}, ${TIMING_WINDOW_MAX_MS}] ms`,
      });
    }
  }
  if (
    chart.perfectWindowMs !== undefined &&
    chart.goodWindowMs !== undefined &&
    Number.isFinite(chart.perfectWindowMs) &&
    Number.isFinite(chart.goodWindowMs) &&
    chart.perfectWindowMs > chart.goodWindowMs
  ) {
    issues.push({
      level: 'error',
      message: `perfectWindowMs ${chart.perfectWindowMs} must be ≤ goodWindowMs ${chart.goodWindowMs}`,
    });
  }

  validateNotes(chart, issues);

  return {
    ok: !issues.some((i) => i.level === 'error'),
    issues,
  };
}

function validateBaseFields(chart: PianoChart, issues: ChartValidationIssue[]): void {
  if (!chart.id) {
    issues.push({ level: 'error', message: 'Chart id is required' });
  }
  const title = chart.title ?? '';
  if (!title.trim()) {
    issues.push({ level: 'error', message: 'Chart title is required' });
  }
  if (title.length > CHART_LIMITS.maxTitleLength) {
    issues.push({
      level: 'error',
      message: `Title exceeds ${CHART_LIMITS.maxTitleLength} chars`,
    });
  }
  if ((chart.description ?? '').length > CHART_LIMITS.maxDescriptionLength) {
    issues.push({
      level: 'error',
      message: `Description exceeds ${CHART_LIMITS.maxDescriptionLength} chars`,
    });
  }
  if (chart.mapper !== undefined) {
    if (typeof chart.mapper !== 'string' || chart.mapper.trim().length === 0) {
      issues.push({
        level: 'error',
        message: 'mapper, when present, must be a non-empty string',
      });
    } else if (chart.mapper.length > CHART_LIMITS.maxMapperLength) {
      issues.push({
        level: 'error',
        message: `Mapper exceeds ${CHART_LIMITS.maxMapperLength} chars`,
      });
    }
  }
  if (!Number.isFinite(chart.bpm) || chart.bpm < 40 || chart.bpm > 500) {
    issues.push({
      level: 'error',
      message: `BPM ${chart.bpm} out of range (40-500)`,
    });
  }
  if (!Number.isFinite(chart.totalBeats) || chart.totalBeats <= 0) {
    issues.push({ level: 'error', message: 'totalBeats must be > 0' });
  }
  if (Number.isFinite(chart.totalBeats) && chart.totalBeats > CHART_LIMITS.maxTotalBeats) {
    issues.push({
      level: 'error',
      message: `totalBeats ${chart.totalBeats} exceeds limit ${CHART_LIMITS.maxTotalBeats}`,
    });
  }
  if (
    !Number.isInteger(chart.difficulty) ||
    chart.difficulty < 1 ||
    chart.difficulty > 5
  ) {
    issues.push({
      level: 'error',
      message: `difficulty ${chart.difficulty} must be integer 1-5`,
    });
  }
  if (!(VARIANTS as ReadonlyArray<string>).includes(chart.variant)) {
    issues.push({
      level: 'error',
      message: `variant ${chart.variant} must be one of ${VARIANTS.join(', ')}`,
    });
  }

  if (!chart.audio || typeof chart.audio.src !== 'string' || chart.audio.src.length === 0) {
    issues.push({ level: 'error', message: 'audio.src is required' });
  } else {
    if (!Number.isFinite(chart.audio.bpm) || chart.audio.bpm !== chart.bpm) {
      issues.push({
        level: 'error',
        message: `audio.bpm (${chart.audio.bpm}) must match chart.bpm (${chart.bpm})`,
      });
    }
    if (!Number.isFinite(chart.audio.offsetMs) || chart.audio.offsetMs < 0) {
      issues.push({ level: 'error', message: 'audio.offsetMs must be a non-negative number' });
    }
    if (!Number.isFinite(chart.audio.durationSec) || chart.audio.durationSec <= 0) {
      issues.push({ level: 'error', message: 'audio.durationSec must be > 0' });
    }
    if ((chart.audio.onsets?.length ?? 0) > CHART_LIMITS.maxOnsets) {
      issues.push({
        level: 'error',
        message: `audio.onsets ${chart.audio.onsets.length} exceeds limit ${CHART_LIMITS.maxOnsets}`,
      });
    }
  }

  for (const key of REQUIRED_PALETTE_KEYS) {
    const value = chart.palette?.[key];
    if (typeof value !== 'string' || !HEX_COLOUR_RE.test(value)) {
      issues.push({
        level: 'error',
        message: `palette.${key} must be a hex colour like #RRGGBB`,
      });
    }
  }
}

function validateNotes(chart: PianoChart, issues: ChartValidationIssue[]): void {
  if (!Array.isArray(chart.notes)) {
    return;
  }
  const ids = new Set<string>();
  const sorted = [...chart.notes].sort((a, b) => a.beat - b.beat);

  // Authoring contract: at most one primary tile (and optionally one
  // dual partner) lives at the same beat. We enforce this by tracking
  // the most recently emitted beat and rejecting any second primary
  // note that lands within an "exact same beat" tolerance.
  // dualPartnerLane is how authors get two simultaneous lanes lit.

  let prevBeat = -Infinity;
  let prevLane: ManiaLane | null = null;
  let prevPartner: ManiaLane | null = null;

  for (const note of sorted) {
    if (!note.id) {
      issues.push({ level: 'error', message: 'Note missing id', beat: note.beat });
      continue;
    }
    if (ids.has(note.id)) {
      issues.push({ level: 'error', message: `Duplicate note id ${note.id}` });
    }
    ids.add(note.id);

    if (!Number.isFinite(note.beat) || note.beat < 0 || note.beat > chart.totalBeats) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} at beat ${note.beat} outside [0, ${chart.totalBeats}]`,
        beat: note.beat,
      });
      continue;
    }
    if (!isManiaLane(note.lane)) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} lane ${note.lane} must be 0..${MANIA_LANE_COUNT - 1}`,
        beat: note.beat,
      });
      continue;
    }

    if (note.dualPartnerLane !== undefined) {
      if (!isManiaLane(note.dualPartnerLane)) {
        issues.push({
          level: 'error',
          message: `Note ${note.id} dualPartnerLane ${note.dualPartnerLane} must be 0..${MANIA_LANE_COUNT - 1}`,
          beat: note.beat,
        });
      } else if (note.dualPartnerLane === note.lane) {
        issues.push({
          level: 'error',
          message: `Note ${note.id} dualPartnerLane must differ from lane ${note.lane}`,
          beat: note.beat,
        });
      }
    }

    if (note.holdEndBeat !== undefined) {
      if (
        !Number.isFinite(note.holdEndBeat) ||
        note.holdEndBeat <= note.beat ||
        note.holdEndBeat > chart.totalBeats
      ) {
        issues.push({
          level: 'error',
          message: `Note ${note.id} holdEndBeat ${note.holdEndBeat} must be in (${note.beat}, ${chart.totalBeats}]`,
          beat: note.beat,
        });
      }
    }

    // Reject overlapping primary notes at the same beat. Authors who
    // need two simultaneous lanes must use dualPartnerLane.
    if (
      prevLane !== null &&
      Math.abs(note.beat - prevBeat) < SAME_BEAT_EPSILON
    ) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} stacks on the previous note at beat ${note.beat}; use dualPartnerLane on the prior note for chord accents`,
        beat: note.beat,
      });
    }
    if (
      prevPartner !== null &&
      Math.abs(note.beat - prevBeat) < SAME_BEAT_EPSILON &&
      (note.lane === prevLane || note.lane === prevPartner)
    ) {
      issues.push({
        level: 'error',
        message: `Note ${note.id} at beat ${note.beat} collides with the previous note's dual partner lane`,
        beat: note.beat,
      });
    }

    prevBeat = note.beat;
    prevLane = note.lane;
    prevPartner = note.dualPartnerLane ?? null;
  }

  validateManiaHoldOverlaps(sorted, issues);
}
